import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Code,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';

import { format, isValid, parseISO } from 'date-fns';

import { Badge, DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';

import ProfileField from 'app/components/UI/ProfileField';

import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchIntegrationSyncRunDetail from 'app/hooks/useFetchIntegrationSyncRunDetail';

import { getRunSyncStatus, SYNC_STATUS_DETAILS } from 'app/utils/integrationSync';
import { getSelectedCompanyPath } from 'app/utils/routes';

interface IntegrationSyncRunDetailDrawerProps {
  syncRunId?: string;
  onClose: () => void;
}

const formatDateValue = (value?: string): string => {
  if (!value) return '—';
  const date = parseISO(value);
  return isValid(date) ? format(date, `${DEFAULT_DATE_FORMAT} HH:mm:ss`) : '—';
};

function IntegrationSyncRunDetailDrawer({
  syncRunId = undefined,
  onClose,
}: IntegrationSyncRunDetailDrawerProps) {
  const { data: run, isLoading, isError } = useFetchIntegrationSyncRunDetail(syncRunId);
  const { data: company } = useFetchCompany(run?.companyId);

  return (
    <Drawer isOpen={!!syncRunId} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Sync run detail</DrawerHeader>
        <DrawerBody>
          {isLoading && (
            <Center py="40px">
              <Spinner color="brand.purple" size="md" />
            </Center>
          )}

          {isError && (
            <Center py="40px">
              <Text textStyle="smallRegular" color="red.500">
                Failed to load this run&apos;s detail. Please try again.
              </Text>
            </Center>
          )}

          {run && (
            <VStack align="stretch" spacing="24px">
              <HStack>
                <Badge
                  value={SYNC_STATUS_DETAILS[getRunSyncStatus(run)].label}
                  status={SYNC_STATUS_DETAILS[getRunSyncStatus(run)].badgeStatus}
                />
                <Text textStyle="smallRegular" color="brand.grey">
                  {run.provider} · {run.syncType}
                </Text>
              </HStack>

              <HStack align="flex-start" spacing="20px">
                <ProfileField label="Synced at" value={formatDateValue(run.syncedAt)} />
                <ProfileField
                  label="Company"
                  value={company?.name ?? run.companyId}
                  link={getSelectedCompanyPath(run.companyId)}
                />
              </HStack>

              <HStack align="flex-start" spacing="20px">
                <ProfileField label="Company ID" value={run.companyId} copyToClipboard />
                {run.deductionsReportId && (
                  <ProfileField
                    label="Deductions report"
                    value={run.deductionsReportId}
                    copyToClipboard
                  />
                )}
              </HStack>

              {run.error && <ProfileField label="Error" value={run.error} />}

              {run.summary && (
                <Box>
                  <Text textStyle="smallSemiBold" color="brand.darkPurple" mb="10px">
                    Summary
                  </Text>
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    p="10px"
                    borderRadius="8px"
                    fontSize="xs"
                  >
                    {JSON.stringify(run.summary, null, 2)}
                  </Code>
                </Box>
              )}

              <Box>
                <Text textStyle="smallSemiBold" color="brand.darkPurple" mb="10px">
                  Outgoing requests ({run.requests.length})
                </Text>

                {run.requests.length === 0 && (
                  <Text textStyle="smallRegular" color="brand.grey">
                    No outgoing requests were logged for this run.
                  </Text>
                )}

                <Accordion allowToggle>
                  {run.requests.map((request, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <AccordionItem key={index}>
                      <AccordionButton>
                        <Flex flex="1" justify="space-between" align="center" pr="10px">
                          <Text textStyle="extraSmallRegular">
                            {request.httpMethod} {request.operation ?? request.requestUrl}
                          </Text>
                          <Badge
                            value={String(request.responseStatus ?? request.exchangeStatus ?? '—')}
                            status={request.exchangeStatus === 'SUCCESS' ? 'active' : 'left'}
                          />
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel>
                        <VStack align="stretch" spacing="12px">
                          <ProfileField label="Request URL" value={request.requestUrl} />
                          <HStack align="flex-start" spacing="20px">
                            <ProfileField
                              label="Started at"
                              value={formatDateValue(request.requestStartedAt)}
                            />
                            <ProfileField
                              label="Duration"
                              value={
                                request.durationMs !== undefined ? `${request.durationMs} ms` : '—'
                              }
                            />
                          </HStack>
                          {request.requestBody && (
                            <Box>
                              <Text textStyle="extraSmallSemiBold" mb="6px">
                                Request body
                              </Text>
                              <Code display="block" whiteSpace="pre-wrap" p="10px" fontSize="xs">
                                {request.requestBody}
                              </Code>
                            </Box>
                          )}
                          {request.responseBody && (
                            <Box>
                              <Text textStyle="extraSmallSemiBold" mb="6px">
                                Response body
                              </Text>
                              <Code display="block" whiteSpace="pre-wrap" p="10px" fontSize="xs">
                                {request.responseBody}
                              </Code>
                            </Box>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default IntegrationSyncRunDetailDrawer;
